import { useEffect, useState } from "react";
import { PageShell } from "../components/Layout/PageShell";
import { Spinner } from "../components/Spinner/Spinner";
import { InlineNotice } from "../components/InlineNotice/InlineNotice";
import { IconLogOut, IconLock, IconStore } from "../components/icons/icons";
import { useAuth } from "../lib/auth/AuthContext";
import type { CognitoRole } from "../lib/auth/jwt";
import { listOwnerTenants, listOwnerMembers, getOwnerAnalytics } from "../lib/api/owner";
import { ApiError } from "../lib/apiClient";
import { SignInView } from "./owner/SignInView";
import { TenantTabs } from "./owner/TenantTabs";
import { TenantWorkspace } from "./owner/TenantWorkspace";
import { AdminWorkspace } from "./admin/AdminWorkspace";
import type { Tenant } from "../types";
import styles from "./OwnerPage.module.css";

export function OwnerPage() {
  const { status, config, configError } = useAuth();

  return (
    <PageShell>
      <div className={styles.page}>
        {status === "loading" && (
          <div className={styles.centerState}>
            <Spinner label="Loading" />
          </div>
        )}
        {status === "config-error" && (
          <div className={styles.centerState}>
            <InlineNotice tone="error">
              {configError ?? "Configuration failed to load."} Reload the page to try again.
            </InlineNotice>
          </div>
        )}
        {status === "unauthenticated" && config && <SignInView />}
        {status === "authenticated" && <AuthenticatedWorkspace />}
      </div>
    </PageShell>
  );
}

const BRAND_LABEL: Record<CognitoRole, string> = {
  administrator: "Administrator workspace",
  owner: "Owner workspace",
  unassigned: "Workspace",
};

function AuthenticatedWorkspace() {
  const { role, email, signOut } = useAuth();

  return (
    <>
      <header className={styles.header}>
        <span className={styles.brand}>
          <IconStore className={styles.brandIcon} width={20} height={20} />
          {BRAND_LABEL[role]}
        </span>
        <div className={styles.headerRight}>
          {email && <span className={styles.email}>{email}</span>}
          <button type="button" className={styles.signOut} onClick={signOut}>
            <IconLogOut width={16} height={16} />
            Sign out
          </button>
        </div>
      </header>
      <div className={styles.body}>
        {role === "administrator" && <AdminWorkspace />}
        {role === "owner" && <OwnerTenants />}
        {role === "unassigned" && <UnassignedNotice />}
      </div>
    </>
  );
}

function UnassignedNotice() {
  const { email } = useAuth();
  return (
    <div className={styles.pending}>
      <InlineNotice tone="info">
        {email ? <strong>{email}</strong> : "Your account"} isn't assigned an administrator or owner role
        yet. Ask an administrator to assign you to a business, then use Sign out above and sign in again —
        a fresh sign-in is required to pick up the change.
      </InlineNotice>
    </div>
  );
}

function SuspendedTenantNotice({ tenantName }: { tenantName: string }) {
  return (
    <div className={styles.suspended} role="status" aria-live="polite">
      <IconLock className={styles.suspendedIcon} width={28} height={28} />
      <h2 className={styles.suspendedHeading}>{tenantName} is suspended</h2>
      <p className={styles.suspendedCopy}>
        An administrator suspended this business. Member data, analytics, and the brand kit are unavailable
        until an administrator reactivates it.
      </p>
    </div>
  );
}

type TenantsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; tenants: Tenant[] };

function OwnerTenants() {
  const { accessToken, invalidateSession } = useAuth();
  const [tenantsState, setTenantsState] = useState<TenantsState>({ status: "loading" });
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    listOwnerTenants(accessToken)
      .then(({ tenants }) => {
        if (cancelled) return;
        setTenantsState({ status: "ready", tenants });
        setSelectedTenantId((current) => current ?? tenants[0]?.tenantId ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          invalidateSession("Your session expired. Sign in again to continue.");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setTenantsState({
            status: "error",
            message: "Access denied. If your access changed recently, sign out and sign in again to refresh it.",
          });
          return;
        }
        setTenantsState({
          status: "error",
          message: err instanceof Error ? err.message : "Couldn't load your businesses.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, invalidateSession]);

  function handleUnauthorized() {
    invalidateSession("Your session expired. Sign in again to continue.");
  }

  /** A successful brand-kit save returns the full updated Tenant; splice it back into the
   * top-level list so the tenant switcher pill and workspace both reflect it without a reload. */
  function handleTenantUpdated(updated: Tenant) {
    setTenantsState((prev) =>
      prev.status === "ready"
        ? { status: "ready", tenants: prev.tenants.map((t) => (t.tenantId === updated.tenantId ? updated : t)) }
        : prev,
    );
  }

  return (
    <>
      {tenantsState.status === "loading" && <Spinner label="Loading your businesses" />}
      {tenantsState.status === "error" && <InlineNotice tone="error">{tenantsState.message}</InlineNotice>}
      {tenantsState.status === "ready" && accessToken && (
        <>
          {tenantsState.tenants.length === 0 ? (
            <InlineNotice tone="info">
              No businesses are assigned to your account yet. Ask an administrator to assign one, then sign
              out and sign in again to refresh your access.
            </InlineNotice>
          ) : (
            <>
              <TenantTabs
                tenants={tenantsState.tenants}
                selectedTenantId={selectedTenantId ?? ""}
                onSelect={setSelectedTenantId}
              />
              {selectedTenantId &&
                (() => {
                  const tenant = tenantsState.tenants.find((t) => t.tenantId === selectedTenantId);
                  if (!tenant) return null;
                  if (tenant.status === "suspended") {
                    return <SuspendedTenantNotice tenantName={tenant.name} />;
                  }
                  return (
                    <TenantWorkspace
                      tenant={tenant}
                      token={accessToken}
                      fetchMembers={listOwnerMembers}
                      fetchAnalytics={getOwnerAnalytics}
                      onUnauthorized={handleUnauthorized}
                      canEditBrand
                      onTenantUpdated={handleTenantUpdated}
                    />
                  );
                })()}
            </>
          )}
        </>
      )}
    </>
  );
}
